import type { DrainPart, FlipperPart, PlungerLanePart, RolloverPart, TableDefinition, TargetPart } from './definition';
import { applyBuiltInLayoutIdentity } from './layoutIdentity';
import { openOfficialPinchRails } from './antiTrapGeometry';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

// Ratios from the original Grand Circuit reference hardware. These proportions
// have already survived the launch/rally physics suite across the full built-in
// size range, so normalization restores proven geometry instead of inventing a
// second lower-playfield layout.
const LOWER_LEFT_X = 17 / 52;
const LOWER_RIGHT_X = 35 / 52;
const LOWER_FLIPPER_Y = 95 / 104;
const LOWER_FLIPPER_LENGTH = 8 / 52;
const DRAIN_Y = 102.5 / 104;
const DRAIN_WIDTH = 13 / 52;
const LANE_X = 45.5 / 52;
const LANE_Y = 7 / 104;
const LANE_WIDTH = 4.5 / 52;
const LANE_HEIGHT = 93 / 104;
const LANE_REST_X = 48 / 52;
const LANE_REST_Y = 96 / 104;
const LANE_DIVIDER_TOP_Y = 18 / 104;
const LANE_DIVIDER_BOTTOM_Y = 97 / 104;
const LANE_STOP_Y = 100 / 104;
const LANE_STOP_WIDTH = 4 / 52;
const RETURN_LEFT_X = 16 / 52;
const RETURN_RIGHT_X = 36 / 52;
const RETURN_Y = 88.75 / 104;

interface RecoveryLaneConfig {
  laneId: string;
  scoreValue: number;
  boostSeconds: number;
}

const RECOVERY_LANE_TABLES: Record<string, RecoveryLaneConfig> = {
  'candy-carnival-v1': { laneId: 'sweet-return', scoreValue: 350, boostSeconds: 3 },
  'deep-sea-dive-v1': { laneId: 'sonar-return', scoreValue: 450, boostSeconds: 8 },
};

function addRecoveryReturnLanes(table: TableDefinition): void {
  const config = RECOVERY_LANE_TABLES[table.id];
  if (!config) return;

  const radius = clamp(table.board.width * 0.022, 1.1, 1.45);
  const specs: Array<Pick<RolloverPart, 'id' | 'center' | 'color'>> = [
    {
      id: 'return-roll-left',
      center: { x: table.board.width * RETURN_LEFT_X, y: table.board.height * RETURN_Y },
      color: table.theme.accentColor,
    },
    {
      id: 'return-roll-right',
      center: { x: table.board.width * RETURN_RIGHT_X, y: table.board.height * RETURN_Y },
      color: table.theme.secondaryColor ?? table.theme.accentColor,
    },
  ];

  for (const spec of specs) {
    if (table.parts.some((part) => part.id === spec.id)) continue;
    table.parts.push({
      ...spec,
      kind: 'rollover',
      scoreValue: config.scoreValue,
      radius,
      laneId: config.laneId,
      scoreBoostSeconds: config.boostSeconds,
      scoreBoostMultiplier: 2,
    });
  }
}

function applyCandySugarJackpot(table: TableDefinition): void {
  if (table.id !== 'candy-carnival-v1') return;

  const jackpot = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  if (jackpot) {
    // Sugar Rush should create a concrete shot to chase, not just inflate every
    // score for a few seconds. Keep the center jackpot hidden until the candy
    // catch fires, then make it a one-shot drop worth a meaningful burst.
    jackpot.appearsAfterPartId = 'catch-candy';
    jackpot.dropDown = true;
    jackpot.hitPoints = 1;
    jackpot.scoreValue = 1800;
    jackpot.destroyScoreValue = 5200;
  }

  const cottonCandy = table.parts.find(
    (part) => part.id === 'bumper-center' && part.kind === 'bumper',
  );
  if (cottonCandy?.kind === 'bumper') {
    // Candy should reward control rather than speed. Turn the middle pop into a
    // broad, soft cotton-candy wheel that absorbs energy instead of behaving like
    // the violent center hazards used by Dragon, Neon, and Cosmic. The existing
    // three-lane Sugar Rush Circuit remains untouched as Candy's mission spine.
    cottonCandy.radius = 4.4;
    cottonCandy.impulse = 34;
    cottonCandy.scoreValue = 1350;
  }
}

function applyDeepSeaPressureSequence(table: TableDefinition): void {
  if (table.id !== 'deep-sea-dive-v1') return;

  const beacon = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  if (beacon) {
    // The trench beacon is physically unavailable until the decompression catch
    // fires. That turns Pressure Surge into a deliberate setup shot: trap the
    // ball, surface the beacon, then cash the one-shot target while 4x is live.
    beacon.appearsAfterPartId = 'catch-deep';
    beacon.dropDown = true;
    beacon.hitPoints = 1;
    beacon.scoreValue = 1800;
    beacon.destroyScoreValue = 5200;
  }

  // Deep Sea's two sonar returns now serve different strategic purposes without
  // changing their proven lower-playfield geometry. Port is the forgiving route:
  // a longer 2x window that rewards controlled recovery. Starboard is the sharp
  // bailout: a shorter 3x burst with a larger immediate award, encouraging the
  // player to capitalize quickly after the harder side of the ascent.
  const portReturn = table.parts.find(
    (part): part is RolloverPart => part.id === 'return-roll-left' && part.kind === 'rollover',
  );
  if (portReturn) {
    portReturn.scoreValue = 600;
    portReturn.scoreBoostSeconds = 7;
    portReturn.scoreBoostMultiplier = 2;
  }
  const starboardReturn = table.parts.find(
    (part): part is RolloverPart => part.id === 'return-roll-right' && part.kind === 'rollover',
  );
  if (starboardReturn) {
    starboardReturn.scoreValue = 900;
    starboardReturn.scoreBoostSeconds = 4;
    starboardReturn.scoreBoostMultiplier = 3;
  }

  const missions = table.rules?.missions;
  if (!missions) return;

  if (!missions.some((mission) => mission.id === 'deep-pressure')) {
    const trenchIndex = missions.findIndex((mission) => mission.id === 'deep-dive');
    const insertAt = trenchIndex >= 0 ? trenchIndex : missions.length;
    missions.splice(insertAt, 0, {
      id: 'deep-pressure',
      name: 'Pressure Lock',
      description: 'Enter the pressure chamber, then break the surfaced trench beacon.',
      trigger: 'target',
      partIds: ['target-2'],
      count: 1,
      bonus: 8500,
    });
  }

  // Once the pressure lock is broken, Deep Sea's finale becomes a staged
  // descent and recovery. The ascent now demands both lower recovery lanes in
  // order, preventing a player from farming the easier side twice.
  const trench = missions.find((mission) => mission.id === 'deep-dive');
  if (trench) {
    trench.description = 'Commit to the long trench ramp once to begin the ascent.';
    trench.count = 1;
    trench.bonus = 10000;
  }

  const oldSurfaceIndex = missions.findIndex((mission) => mission.id === 'deep-surface');
  if (oldSurfaceIndex >= 0) missions.splice(oldSurfaceIndex, 1);

  if (!missions.some((mission) => mission.id === 'deep-surface-port')) {
    const trenchIndex = missions.findIndex((mission) => mission.id === 'deep-dive');
    const insertAt = trenchIndex >= 0 ? trenchIndex + 1 : missions.length;
    missions.splice(insertAt, 0,
      {
        id: 'deep-surface-port',
        name: 'Port Ascent',
        description: 'Recover through port for a steadier 2x sonar window.',
        trigger: 'rollover',
        partIds: ['return-roll-left'],
        count: 1,
        bonus: 6000,
      },
      {
        id: 'deep-surface-starboard',
        name: 'Starboard Ascent',
        description: 'Break the surface through starboard and cash the short 3x surge.',
        trigger: 'rollover',
        partIds: ['return-roll-right'],
        count: 1,
        bonus: 7000,
      },
    );
  }
  if (table.rules) table.rules.completionBonus = 25000;
}

function applyNeonOverdriveBreakerChain(table: TableDefinition): void {
  if (table.id !== 'neon-rush-v1') return;

  const specs = [
    { id: 'target-1', scoreValue: 1300, destroyScoreValue: 1200, appearsAfterPartId: 'catch-neon' },
    { id: 'target-2', scoreValue: 1900, destroyScoreValue: 2200, appearsAfterPartId: 'target-1' },
    { id: 'target-3', scoreValue: 2800, destroyScoreValue: 3600, appearsAfterPartId: 'target-2' },
  ] as const;

  for (const spec of specs) {
    const breaker = table.parts.find(
      (part): part is TargetPart => part.id === spec.id && part.kind === 'target',
    );
    if (!breaker) continue;
    breaker.dropDown = true;
    breaker.hitPoints = 1;
    breaker.scoreValue = spec.scoreValue;
    breaker.destroyScoreValue = spec.destroyScoreValue;
    breaker.appearsAfterPartId = spec.appearsAfterPartId;
  }

  const missions = table.rules?.missions;
  if (!missions) return;
  const targetIndex = missions.findIndex((mission) => mission.id === 'neon-targets');
  if (targetIndex >= 0) missions.splice(targetIndex, 1);

  // The physical catch already gates the first one-shot breaker, so surface that
  // ignition step in mission/HUD progression before asking the player to hit a
  // target that does not exist yet. The existing 4-second / 5x Overdrive window
  // remains the strategic payoff for clearing the sequential breaker chain fast.
  if (!missions.some((mission) => mission.id === 'neon-overdrive')) {
    missions.unshift({
      id: 'neon-overdrive',
      name: 'Ignite Overdrive',
      description: 'Trap the ball in Overdrive to energize and expose the first breaker.',
      trigger: 'kicker',
      partIds: ['catch-neon'],
      count: 1,
      bonus: 5000,
    });
  }

  const existingBreaker = missions.findIndex((mission) => mission.id === 'neon-breaker-1');
  if (existingBreaker < 0) {
    const ignitionIndex = missions.findIndex((mission) => mission.id === 'neon-overdrive');
    missions.splice(ignitionIndex >= 0 ? ignitionIndex + 1 : 0, 0,
      {
        id: 'neon-breaker-1',
        name: 'Trip Breaker One',
        description: 'Drop the energized first breaker before Overdrive cools.',
        trigger: 'target',
        partIds: ['target-1'],
        count: 1,
        bonus: 6500,
      },
      {
        id: 'neon-breaker-2',
        name: 'Trip Breaker Two',
        description: 'Chain immediately into the second breaker.',
        trigger: 'target',
        partIds: ['target-2'],
        count: 1,
        bonus: 8500,
      },
      {
        id: 'neon-breaker-3',
        name: 'Break the Limit',
        description: 'Drop the final high-voltage breaker to unlock the reactor run.',
        trigger: 'target',
        partIds: ['target-3'],
        count: 1,
        bonus: 11500,
      },
    );
  }

  // The three reactor bumpers already have deliberately different physical
  // contact profiles, but the old eight-pop mission let the player farm any one
  // of them. Route the post-breaker phase through coolant -> ignition -> discharge
  // instead, so each asymmetric reactor state becomes a required decision before
  // the final ramp sprint.
  const reactorIndex = missions.findIndex((mission) => mission.id === 'neon-pops');
  if (reactorIndex >= 0) {
    missions.splice(reactorIndex, 1,
      {
        id: 'neon-coolant',
        name: 'Prime Coolant',
        description: 'Tag the broad coolant coil to stabilize the energized reactor.',
        trigger: 'bumper',
        partIds: ['bumper-left'],
        count: 1,
        bonus: 4500,
      },
      {
        id: 'neon-ignition-core',
        name: 'Punch Ignition',
        description: 'Drive the volatile center core once and survive its violent kick.',
        trigger: 'bumper',
        partIds: ['bumper-center'],
        count: 1,
        bonus: 7000,
      },
      {
        id: 'neon-discharge',
        name: 'Dump the Charge',
        description: 'Clip the fast discharge coil to route the reactor into the ramp sprint.',
        trigger: 'bumper',
        partIds: ['bumper-right'],
        count: 1,
        bonus: 5500,
      },
    );
  }
  if (table.rules) table.rules.completionBonus = 32000;
}

function applyClockworkEscapementSequence(table: TableDefinition): void {
  if (table.id !== 'clockwork-district-v1') return;

  const automaton = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  if (automaton) {
    // The clockwork catch now acts as a winding key: the automaton remains
    // physically unavailable until the player traps the ball and winds the
    // mechanism, turning a generic catch into the table's setup interaction.
    automaton.appearsAfterPartId = 'catch-clockwork';
    automaton.scoreValue = 1200;
    automaton.destroyScoreValue = 5200;
  }

  const missions = table.rules?.missions;
  if (!missions) return;

  // Make the physical winding key visible in mission/HUD progression. Because
  // the automaton is already gated behind this kicker, the new opening step
  // describes the real mechanism without introducing a second source of truth.
  if (!missions.some((mission) => mission.id === 'clock-winding')) {
    const automatonIndex = missions.findIndex((mission) => mission.id === 'clock-robot');
    missions.splice(automatonIndex >= 0 ? automatonIndex : 0, 0, {
      id: 'clock-winding',
      name: 'Wind the Key',
      description: 'Trap the ball in the winding cup to start the mechanism and expose the automaton.',
      trigger: 'kicker',
      partIds: ['catch-clockwork'],
      count: 1,
      bonus: 5500,
    });
  }

  const gearsIndex = missions.findIndex((mission) => mission.id === 'clock-gears');
  if (gearsIndex >= 0) {
    // Replace the interchangeable four-ramp grind with a deterministic two-stage
    // escapement cycle. The player has to load the main gear train first and then
    // transfer that momentum through the upper mechanism, giving the two ramps
    // separate jobs instead of treating them as cosmetic substitutes.
    missions.splice(gearsIndex, 1,
      {
        id: 'clock-mainspring',
        name: 'Charge the Mainspring',
        description: 'Drive the main gear train twice to store enough spring tension.',
        trigger: 'ramp',
        partIds: ['ramp-main'],
        count: 2,
        bonus: 6500,
      },
      {
        id: 'clock-escapement',
        name: 'Sync the Escapement',
        description: 'Transfer the charge through the upper mechanism twice.',
        trigger: 'ramp',
        partIds: ['ramp-upper-loop'],
        count: 2,
        bonus: 7500,
      },
    );
  }

  const pressureIndex = missions.findIndex((mission) => mission.id === 'clock-pops');
  if (pressureIndex >= 0) {
    // Once the escapement is synchronized, the finale should test the machine as
    // a mechanism rather than reward nine interchangeable pop hits. Balance all
    // three governor weights exactly once, then send the upper escapement around
    // one last time to release the stored pressure and finish the cycle.
    missions.splice(pressureIndex, 1,
      {
        id: 'clock-governor',
        name: 'Balance the Governor',
        description: 'Strike all three regulator bumpers to balance the governor.',
        trigger: 'bumper',
        partIds: ['bumper-left', 'bumper-center', 'bumper-right'],
        count: 3,
        bonus: 9000,
      },
      {
        id: 'clock-release',
        name: 'Release the Escapement',
        description: 'Send the upper mechanism around once more to vent the stored pressure.',
        trigger: 'ramp',
        partIds: ['ramp-upper-loop'],
        count: 1,
        bonus: 10500,
      },
    );
  }
  if (table.rules) table.rules.completionBonus = 35000;
}

function applyFrontierWantedBank(table: TableDefinition): void {
  if (table.id !== 'frontier-run-v1') return;

  const specs = [
    { id: 'target-1', scoreValue: 900, destroyScoreValue: 1000, appearsAfterPartId: 'catch-frontier' },
    { id: 'target-2', scoreValue: 1300, destroyScoreValue: 2000, appearsAfterPartId: 'target-1' },
    { id: 'target-3', scoreValue: 2000, destroyScoreValue: 3500, appearsAfterPartId: 'target-2' },
  ] as const;

  for (const spec of specs) {
    const wanted = table.parts.find(
      (part): part is TargetPart => part.id === spec.id && part.kind === 'target',
    );
    if (!wanted) continue;
    wanted.dropDown = true;
    wanted.hitPoints = 1;
    wanted.scoreValue = spec.scoreValue;
    wanted.destroyScoreValue = spec.destroyScoreValue;
    wanted.appearsAfterPartId = spec.appearsAfterPartId;
  }

  const missions = table.rules?.missions;
  if (!missions) return;

  // Give the frontier catch a concrete role in the chase. The player must first
  // gather the posse, which physically reveals the first outlaw; the existing
  // one-shot wanted bank then carries the pursuit forward target by target.
  if (!missions.some((mission) => mission.id === 'frontier-posse')) {
    const wantedIndex = missions.findIndex((mission) => mission.id === 'frontier-targets');
    missions.splice(wantedIndex >= 0 ? wantedIndex : 0, 0, {
      id: 'frontier-posse',
      name: 'Round Up the Posse',
      description: 'Trap the ball at the sheriff post to assemble the posse and expose the first outlaw.',
      trigger: 'kicker',
      partIds: ['catch-frontier'],
      count: 1,
      bonus: 5000,
    });
  }

  const railIndex = missions.findIndex((mission) => mission.id === 'frontier-rails');
  if (railIndex >= 0) {
    // Turn the railway pair into a real there-and-back journey. The lower route
    // sends the posse outbound; only after that shot is complete does the upper
    // rail become the mission objective for the return leg. The ramp geometry is
    // unchanged, so this adds route identity without touching proven physics.
    missions.splice(railIndex, 1,
      {
        id: 'frontier-outbound',
        name: 'Outbound Express',
        description: 'Take the main railway out of town to chase the gang.',
        trigger: 'ramp',
        partIds: ['ramp-main'],
        count: 1,
        bonus: 6500,
      },
      {
        id: 'frontier-return',
        name: 'Highline Return',
        description: 'Bring the posse home over the upper railway.',
        trigger: 'ramp',
        partIds: ['ramp-upper-loop'],
        count: 1,
        bonus: 9000,
      },
    );
  }
  if (table.rules) table.rules.completionBonus = 30000;
}

function applyHauntedDeadBells(table: TableDefinition): void {
  if (table.id !== 'haunted-keep-v1') return;

  const bells = [
    { id: 'bumper-left', radius: 3.35, impulse: 48, scoreValue: 800 },
    { id: 'bumper-center', radius: 3.05, impulse: 74, scoreValue: 1500 },
    { id: 'bumper-right', radius: 3.2, impulse: 59, scoreValue: 1050 },
  ] as const;

  // Haunted's guardians already form a strong boss ladder. The dead bells add
  // a different post-combat rhythm: the left bell is a broad, soft control hit,
  // the center bell is a violent high-value toll, and the right bell sits between
  // them. The ritual therefore asks for three materially different contacts
  // instead of another generic pop count.
  for (const spec of bells) {
    const bell = table.parts.find((part) => part.id === spec.id);
    if (bell?.kind !== 'bumper') continue;
    bell.radius = spec.radius;
    bell.impulse = spec.impulse;
    bell.scoreValue = spec.scoreValue;
  }

  const missions = table.rules?.missions;
  if (!missions || missions.some((mission) => mission.id === 'haunt-dead-bells')) return;

  const escapeIndex = missions.findIndex((mission) => mission.id === 'haunt-breach-route');
  const insertAt = escapeIndex >= 0 ? escapeIndex : missions.length;
  missions.splice(insertAt, 0, {
    id: 'haunt-dead-bells',
    name: 'Toll the Dead Bells',
    description: 'Ring all three spirit bells once to break the curse before the escape.',
    trigger: 'bumper',
    partIds: ['bumper-left', 'bumper-center', 'bumper-right'],
    count: 3,
    bonus: 10000,
  });
  if (table.rules) table.rules.completionBonus = 39000;
}

function applyDragonBossGauntlet(table: TableDefinition): void {
  if (table.id !== 'dragon-keep-v1') return;

  const gate = table.parts.find(
    (part): part is TargetPart => part.id === 'target-1' && part.kind === 'target',
  );
  const crystal = table.parts.find(
    (part): part is TargetPart => part.id === 'target-2' && part.kind === 'target',
  );
  const guardian = table.parts.find(
    (part): part is TargetPart => part.id === 'target-3' && part.kind === 'target',
  );

  // The dragon catch is the awakening stroke for the boss gauntlet. Keep the
  // stone gate physically dormant until the player captures the ball, then let
  // the existing gate -> crystal -> guardian chain unfold exactly as before.
  if (gate) gate.appearsAfterPartId = 'catch-dragon';
  if (crystal) crystal.appearsAfterPartId = 'target-1';
  if (guardian) guardian.appearsAfterPartId = 'target-2';

  const missions = table.rules?.missions;
  if (!missions) return;

  if (!missions.some((mission) => mission.id === 'dragon-awakening')) {
    const gateIndex = missions.findIndex((mission) => mission.id === 'dragon-gate');
    missions.splice(gateIndex >= 0 ? gateIndex : 0, 0, {
      id: 'dragon-awakening',
      name: 'Kindle the Keep',
      description: 'Trap the ball in the dragon catch to ignite the keep and expose the stone gate.',
      trigger: 'kicker',
      partIds: ['catch-dragon'],
      count: 1,
      bonus: 6000,
    });
  }

  const flightIndex = missions.findIndex((mission) => mission.id === 'dragon-rails');
  if (flightIndex >= 0) {
    // Dragon Flight now has a real launch and airborne phase. The main ramp is
    // the takeoff stroke; only after that succeeds does the upper four-flipper
    // route become the active objective, turning the elevated playfield into a
    // required part of the finale instead of an interchangeable shortcut.
    missions.splice(flightIndex, 1,
      {
        id: 'dragon-takeoff',
        name: 'Ignite the Takeoff',
        description: 'Launch from the lower fire ramp to take to the sky.',
        trigger: 'ramp',
        partIds: ['ramp-main'],
        count: 1,
        bonus: 9000,
      },
      {
        id: 'dragon-flight',
        name: 'Ride the Firestorm',
        description: 'Circle the upper flight path twice before landing the victory.',
        trigger: 'ramp',
        partIds: ['ramp-upper-loop'],
        count: 2,
        bonus: 14000,
      },
    );
  }
  if (table.rules) table.rules.completionBonus = 43000;
}

/**
 * Keep the essential lower playfield hardware in a conventional, reachable
 * layout after a built-in table has been resized or reshaped.
 *
 * Built-ins intentionally vary their upper playfield, but every official table
 * must retain one dependable lower flipper pair, a centered drain, and a valid
 * shooter lane. This function only normalizes those essentials; it does not
 * flatten the table-specific ramps, targets, bumpers, missions, or upper flippers.
 */
export function normalizeBuiltInHardware(table: TableDefinition): TableDefinition {
  const { width, height } = table.board;
  const lowerY = height * LOWER_FLIPPER_Y;
  const flipperLength = clamp(width * LOWER_FLIPPER_LENGTH, 7.5, 11);

  const left = table.parts.find(
    (part): part is FlipperPart => part.id === 'flip-l' && part.kind === 'flipper-left',
  );
  const right = table.parts.find(
    (part): part is FlipperPart => part.id === 'flip-r' && part.kind === 'flipper-right',
  );

  if (left) {
    left.pivot = { x: width * LOWER_LEFT_X, y: lowerY };
    left.length = flipperLength;
    left.restAngleDeg = 30;
    left.sweepDeg = 56;
  }
  if (right) {
    right.pivot = { x: width * LOWER_RIGHT_X, y: lowerY };
    right.length = flipperLength;
    right.restAngleDeg = 30;
    right.sweepDeg = 56;
  }

  const drain = table.parts.find((part): part is DrainPart => part.kind === 'drain');
  if (drain) {
    drain.center = { x: width / 2, y: height * DRAIN_Y };
    drain.width = clamp(width * DRAIN_WIDTH, 12, width * 0.34);
  }

  const lanes = table.parts.filter((part): part is PlungerLanePart => part.kind === 'plunger-lane');
  const lane = lanes[0];
  if (lane) {
    const laneX = width * LANE_X;
    const laneY = height * LANE_Y;
    const laneWidth = clamp(width * LANE_WIDTH, 4.2, 6.2);
    const laneHeight = height * LANE_HEIGHT;
    const rest = { x: width * LANE_REST_X, y: height * LANE_REST_Y };
    lane.bounds = { x: laneX, y: laneY, width: laneWidth, height: laneHeight };
    lane.restPosition = rest;
    lane.stop = {
      center: { x: rest.x, y: height * LANE_STOP_Y },
      width: width * LANE_STOP_WIDTH,
      thickness: clamp(width * 0.019, 0.8, 1.4),
    };
    if (lane.divider) {
      lane.divider.points = [
        { x: laneX, y: height * LANE_DIVIDER_TOP_Y },
        { x: laneX, y: height * LANE_DIVIDER_BOTTOM_Y },
      ];
    }
    if (lane.launchPath && lane.launchPath.length > 0) {
      lane.launchPath[0] = { ...rest };
    }
  }

  // Deep Sea Dive is intentionally the most forgiving official machine. Its
  // slower playfield and sonar-return lanes pair with one extra recoverable
  // nudge, creating a real rescue-oriented control identity rather than only a
  // softer visual theme.
  if (table.id === 'deep-sea-dive-v1') table.board.nudgeCount = 4;

  // Neon Rush is the opposite end of the control spectrum: its steep, narrow,
  // high-energy layout gets exactly one bailout per ball. The tighter nudge
  // budget turns its speed into a real survival constraint instead of merely a
  // faster-looking version of the same recovery rules.
  if (table.id === 'neon-rush-v1') table.board.nudgeCount = 1;

  addRecoveryReturnLanes(table);
  applyCandySugarJackpot(table);
  applyDeepSeaPressureSequence(table);
  applyNeonOverdriveBreakerChain(table);
  applyClockworkEscapementSequence(table);
  applyFrontierWantedBank(table);
  applyHauntedDeadBells(table);
  applyDragonBossGauntlet(table);
  return openOfficialPinchRails(applyBuiltInLayoutIdentity(table));
}

export function officialLowerHardwareIssues(table: TableDefinition): string[] {
  const issues: string[] = [];
  const { width, height } = table.board;
  const lowerLeft = table.parts.find(
    (part): part is FlipperPart => part.id === 'flip-l' && part.kind === 'flipper-left',
  );
  const lowerRight = table.parts.find(
    (part): part is FlipperPart => part.id === 'flip-r' && part.kind === 'flipper-right',
  );

  if (!lowerLeft) issues.push('missing lower left flipper');
  if (!lowerRight) issues.push('missing lower right flipper');

  for (const [label, flipper] of [['left', lowerLeft], ['right', lowerRight]] as const) {
    if (!flipper) continue;
    if (flipper.pivot.y < height * 0.82 || flipper.pivot.y > height * 0.95) {
      issues.push(`${label} lower flipper is outside the lower playfield`);
    }
    if (flipper.length < width * 0.11 || flipper.length > width * 0.22) {
      issues.push(`${label} lower flipper has an unreasonable reach`);
    }
  }

  if (lowerLeft && lowerRight) {
    if (lowerLeft.pivot.x >= lowerRight.pivot.x) issues.push('lower flippers are crossed');
    const centerGap = lowerRight.pivot.x - lowerLeft.pivot.x;
    if (centerGap < width * 0.22 || centerGap > width * 0.46) {
      issues.push('lower flipper spacing is not playable');
    }
  }

  const drainParts = table.parts.filter((part): part is DrainPart => part.kind === 'drain');
  if (drainParts.length !== 1) issues.push('table must have exactly one drain');
  const drain = drainParts[0];
  if (drain && drain.center.y <= (lowerLeft?.pivot.y ?? 0)) {
    issues.push('drain must sit below the lower flippers');
  }

  const lanes = table.parts.filter((part): part is PlungerLanePart => part.kind === 'plunger-lane');
  if (lanes.length !== 1) issues.push('table must have exactly one plunger lane');
  const lane = lanes[0];
  if (lane) {
    if (lane.bounds.x < width * 0.7) issues.push('plunger lane is not on the shooter side');
    if ((lane.restPosition?.y ?? 0) < height * 0.8) issues.push('plunger rest is too high');
  }

  return issues;
}
